import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/TDSynnex.css";
import tdSynnexLogo from "../../assets/images/partners/td-synnex-logo.png";

const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);

const categories = [
  {
    icon: "☁️", name: "Cloud & Hyperscalers",
    vendors: ["Microsoft Azure", "AWS", "Google Cloud", "IBM Cloud", "Oracle Cloud"],
    desc: "Cloud marketplace subscriptions, credits, and professional services through TD SYNNEX's cloud aggregation platform.",
  },
  {
    icon: "🔒", name: "Networking & Security",
    vendors: ["Cisco", "Fortinet", "Palo Alto", "CheckPoint", "Juniper", "Aruba"],
    desc: "Hardware, software, and subscriptions from the world's leading networking and security vendors, shipped with official warranties.",
  },
  {
    icon: "🖥️", name: "Servers & Storage",
    vendors: ["Dell EMC", "HPE", "Lenovo", "Supermicro", "NetApp", "Pure Storage"],
    desc: "Configure-to-order servers, storage arrays, and HCI appliances with factory integration services before delivery.",
  },
  {
    icon: "📋", name: "Software & Licensing",
    vendors: ["Microsoft", "Adobe", "VMware", "Citrix", "Veeam", "Red Hat", "Sophos"],
    desc: "Volume licensing, EAs, subscriptions, and perpetual licenses with full compliance tracking and renewal management.",
  },
  {
    icon: "📱", name: "Mobility & Endpoints",
    vendors: ["Apple", "HP Inc", "Lenovo", "Samsung", "Zebra", "Datalogic"],
    desc: "From desktops and laptops to rugged handhelds and mobile devices — all configured and shipped with imaging services.",
  },
  {
    icon: "🖨️", name: "Print & Document",
    vendors: ["HP", "Xerox", "Canon", "Epson", "Lexmark", "Brother"],
    desc: "Managed print solutions, hardware, supplies, and maintenance contracts through TD SYNNEX's print services program.",
  },
];

const supplyNodes = [
  { icon: "🌐", name: "Global Vendor Relationships", desc: "Direct partnerships with 1,500+ technology vendors across every category.", tag: "1,500+ Vendors" },
  { icon: "🏭", name: "Regional Distribution Centers", desc: "Strategically located warehouses enabling next-day delivery across all major markets.", tag: "100+ Countries" },
  { icon: "⚙️", name: "Configuration & Integration", desc: "Factory-configure hardware with your exact specifications before shipment.", tag: "Built-to-Order" },
  { icon: "☁️", name: "Cloud Aggregation Platform", desc: "Single-pane procurement for Azure, AWS, and Google Cloud marketplace subscriptions.", tag: "Multi-Cloud" },
  { icon: "📦", name: "Logistics & Drop-Ship", desc: "Direct-to-site fulfillment with white-glove delivery and asset tagging services.", tag: "Direct Delivery" },
];

const services = [
  { icon: "⚙️", title: "Configuration & Imaging", desc: "Factory-configure devices with your OS image, applications, and security policies before delivery." },
  { icon: "🏷️", title: "Asset Tagging & Tracking", desc: "Asset tags, serial number capture, and CMDB-ready reports for every item shipped." },
  { icon: "🚚", title: "Logistics & Drop Shipping", desc: "Direct-to-site or white-glove delivery to any location, nationally or internationally." },
  { icon: "🔄", title: "Refresh & Disposal (ITAD)", desc: "Certified IT asset disposition with documented data destruction certificates." },
];

const whyCards = [
  { icon: "💰", title: "Competitive Pricing", desc: "TD SYNNEX's purchasing volume with 1,500+ vendors translates to better pricing for ALG Data Guard clients than direct purchasing." },
  { icon: "✅", title: "Authorised Warranties", desc: "Every product sourced through TD SYNNEX carries full, official manufacturer warranty — no grey-market risk." },
  { icon: "⚡", title: "Speed to Delivery", desc: "Regional distribution centers and established logistics networks enable delivery within days, anywhere in the region." },
  { icon: "📊", title: "Single Source of Truth", desc: "One relationship for hardware, software, cloud, and services — simplifying procurement and accounts payable." },
];

const journey = [
  { num: "01", icon: "📋", title: "Requirements Capture", desc: "ALG Data Guard works with your team to define the exact specifications, quantities, and delivery requirements for each technology item." },
  { num: "02", icon: "💬", title: "Quotation & Pricing", desc: "We leverage TD SYNNEX's pricing APIs and volume agreements to deliver competitive, fully-costed quotations with no hidden fees." },
  { num: "03", icon: "🏭", title: "Configure & Stage", desc: "TD SYNNEX's integration facilities configure devices with your images, policies, and asset tags before shipping — ready to deploy on arrival." },
  { num: "04", icon: "🚀", title: "Deploy & Support", desc: "ALG Data Guard handles on-site deployment, integration, and handover — with ongoing support, refresh planning, and ITAD lifecycle services." },
];

const industries = [
  { icon: "🏦", name: "Financial Services", desc: "Sourcing trading workstations, secure networking hardware, and compliance-grade storage arrays with full audit trails." },
  { icon: "🏥", name: "Healthcare", desc: "Medical-grade devices, clinical workstations, and HIPAA-compliant infrastructure sourced with validated warranty coverage." },
  { icon: "🏭", name: "Manufacturing & Industry", desc: "Ruggedized endpoints, industrial networking hardware, and OT-grade servers for factory and plant environments." },
  { icon: "🏛️", name: "Government & Public Sector", desc: "Government-approved hardware sourcing with certified supply chains, asset documentation, and disposal compliance." },
  { icon: "🎓", name: "Education", desc: "Volume device procurement, imaging services, and managed refresh cycles for campus-wide technology programmes." },
  { icon: "🏢", name: "Enterprise & Corporate", desc: "End-to-end technology refresh, data centre hardware, and software licensing for large enterprise environments." },
];

const faqData = [
  {
    q: "What is TD SYNNEX and why does ALG Data Guard partner with them?",
    a: "TD SYNNEX is the world's largest IT distributor — formed by the merger of Tech Data and Synnex Corporation. With direct relationships with 1,500+ technology vendors and operations in 100+ countries, TD SYNNEX provides ALG Data Guard with access to virtually any IT product at competitive pricing, with official manufacturer warranties. This means our clients benefit from the combined purchasing power of a $57B distributor alongside ALG Data Guard's technical expertise and deployment services.",
  },
  {
    q: "Can ALG Data Guard source any IT product through TD SYNNEX?",
    a: "Almost. TD SYNNEX's catalogue of 200,000+ products spans hardware (servers, networking, storage, endpoints, print), software (perpetual, subscription, volume licensing), and cloud marketplace credits and subscriptions. If you need a specific technology product, ALG Data Guard can source it through TD SYNNEX with an official warranty and competitive pricing. We'll advise honestly if a product isn't available or if an alternative route is more appropriate.",
  },
  {
    q: "Does sourcing through TD SYNNEX affect product warranties?",
    a: "No. TD SYNNEX is an authorized distributor for every vendor in their catalogue. Products sourced through TD SYNNEX carry full, official manufacturer warranties — identical to purchasing direct from the vendor. You receive all warranty registration, support entitlements, and manufacturer SLAs without any restrictions. Grey-market or unauthorized re-seller risk does not apply to products sourced through TD SYNNEX's authorized supply chain.",
  },
  {
    q: "Can TD SYNNEX configure devices before delivery to our site?",
    a: "Yes. TD SYNNEX's integration and configuration facilities can pre-configure devices with your OS image, software applications, security policies, and asset tags before shipping. This means devices arrive at your site ready to deploy — dramatically reducing on-site deployment time and engineering costs. ALG Data Guard coordinates the configuration specification and validates output quality before delivery.",
  },
  {
    q: "How does ALG Data Guard handle IT asset disposal (ITAD) for end-of-life equipment?",
    a: "ALG Data Guard, in conjunction with TD SYNNEX's ITAD services, provides certified IT asset disposition for end-of-life technology. This includes secure data destruction (with documented certificates), hardware refurbishment or recycling, and environmental compliance documentation. We can coordinate ITAD as part of a technology refresh project — removing and disposing of old equipment at the same time as deploying new.",
  },
];

export default function TDSynnex() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="tds-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={tdSynnexLogo} alt="TD SYNNEX" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Global Distribution Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × TD SYNNEX</h1>
            <p className="pd-identity-desc">
              TD SYNNEX is the world's largest IT distributor — formed by the merger of Tech Data and
              Synnex Corporation. With 200,000+ products from 1,500+ vendors across 100+ countries,
              TD SYNNEX is ALG Data Guard's primary supply chain partner for sourcing, configuring,
              and delivering virtually any technology product with official warranties and speed.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="tds-hero tds-grid-bg">
        <div className="tds-hero__grad" aria-hidden="true" />
        <div className="tds-hero__inner">
          <div>
            <div className="tds-hero__badge">
              <span className="tds-hero__badge-item">Authorized Reseller</span>
              <span className="tds-hero__badge-item">Global Distribution</span>
            </div>
            <h2 className="tds-hero__title">
              Any Technology.<br />
              <span>Any Vendor. Anywhere.</span>
            </h2>
            <p className="tds-hero__body">
              As an authorized TD SYNNEX reseller, ALG Data Guard can source virtually any IT product
              from any of 1,500+ vendors — with official warranties, competitive pricing, and
              configuration services. From a single laptop to a full data centre build, we handle
              procurement, delivery, and deployment end-to-end.
            </p>
            <div className="tds-hero__actions">
              <button className="tds-btn tds-btn--orange" onClick={() => navigate("/contact")}>
                Request a Product Quote <ArrowIcon />
              </button>
              <button className="tds-btn tds-btn--ghost" onClick={() => navigate("/contact")}>
                Talk to a Specialist
              </button>
            </div>
            <div className="tds-hero__kpis">
              {[
                { val: "200K+", label: "Products Available" },
                { val: "1,500+", label: "Technology Vendors" },
                { val: "100+", label: "Countries Covered" },
                { val: "$57B", label: "Annual Revenue" },
              ].map((k) => (
                <div key={k.label} className="tds-hero__kpi">
                  <div className="tds-hero__kpi-val">{k.val}</div>
                  <div className="tds-hero__kpi-label">{k.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Supply Chain Visual */}
          <div className="tds-hero__visual">
            {supplyNodes.map((node) => (
              <div key={node.name} className="tds-supply-node">
                <span className="tds-supply-node__icon">{node.icon}</span>
                <div>
                  <div className="tds-supply-node__name">{node.name}</div>
                  <p className="tds-supply-node__desc">{node.desc}</p>
                </div>
                <span className="tds-supply-node__tag">{node.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics Band ── */}
      <section className="tds-metrics-band">
        <div className="tds-shell">
          <div className="tds-metrics-band__inner">
            {[
              { val: "200,000+", label: "Products in Catalogue" },
              { val: "1,500+", label: "Vendor Partners" },
              { val: "100+", label: "Countries of Operation" },
              { val: "$57B", label: "Annual Revenue" },
            ].map((m) => (
              <div key={m.label}>
                <div className="tds-metric__val">{m.val}</div>
                <div className="tds-metric__label">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Technology Categories ── */}
      <section className="tds-categories">
        <div className="tds-shell">
          <div className="tds-categories__head">
            <div className="tds-tag tds-tag--dark"><span className="tds-dot" /> Technology Portfolio</div>
            <h2 className="tds-title">Every Technology Category. One Partner.</h2>
            <p className="tds-sub tds-sub--dark" style={{ maxWidth: "620px", margin: "0 auto" }}>
              TD SYNNEX's portfolio covers every area of modern IT. Through our partnership, ALG Data Guard
              can source any technology product your organisation needs — from any vendor, at any scale.
            </p>
          </div>
          <div className="tds-categories__grid">
            {categories.map((cat) => (
              <div key={cat.name} className="tds-cat-card">
                <div className="tds-cat-card__header">
                  <span className="tds-cat-card__icon">{cat.icon}</span>
                  <h3 className="tds-cat-card__name">{cat.name}</h3>
                </div>
                <p className="tds-cat-card__desc">{cat.desc}</p>
                <div className="tds-cat-card__vendors">
                  {cat.vendors.map((v) => <span key={v} className="tds-vendor-tag">{v}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Value-Add Services ── */}
      <section className="tds-services">
        <div className="tds-shell">
          <div className="tds-services__inner">
            <img
              src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=900&q=80"
              alt="TD SYNNEX logistics and configuration"
              className="tds-services__img"
            />
            <div>
              <div className="tds-tag tds-tag--orange"><span className="tds-dot" /> Value-Add Services</div>
              <h2 className="tds-title tds-services__title">More Than Just a Supplier</h2>
              <p className="tds-services__body">
                TD SYNNEX's distribution excellence, combined with ALG Data Guard's technical and
                project delivery capability, means you receive a complete technology procurement and
                deployment service — not just boxes shipped to your door.
              </p>
              <div className="tds-services__list">
                {services.map((s) => (
                  <div key={s.title} className="tds-service-item">
                    <span className="tds-service-item__icon">{s.icon}</span>
                    <div>
                      <div className="tds-service-item__title">{s.title}</div>
                      <p className="tds-service-item__desc">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="tds-btn tds-btn--orange" onClick={() => navigate("/contact")}>
                Source Technology Products <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Partner ── */}
      <section className="tds-why">
        <div className="tds-shell">
          <div className="tds-why__head">
            <div className="tds-tag tds-tag--dark"><span className="tds-dot" /> Why TD SYNNEX</div>
            <h2 className="tds-title">Why Procure Through ALG Data Guard & TD SYNNEX?</h2>
            <p className="tds-sub tds-sub--dark" style={{ maxWidth: "600px", margin: "0 auto" }}>
              Sourcing technology through ALG Data Guard and TD SYNNEX gives your organisation
              access to competitive pricing, authorised supply chains, and end-to-end deployment services.
            </p>
          </div>
          <div className="tds-why__grid">
            {whyCards.map((w) => (
              <div key={w.title} className="tds-why-card">
                <span className="tds-why-card__icon">{w.icon}</span>
                <div className="tds-why-card__title">{w.title}</div>
                <p className="tds-why-card__desc">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Procurement Journey ── */}
      <section className="tds-journey">
        <div className="tds-shell">
          <div className="tds-journey__head">
            <div className="tds-tag tds-tag--orange" style={{ margin: "0 auto 1rem" }}><span className="tds-dot" /> How It Works</div>
            <h2 className="tds-title">From Requirement to Deployment in Four Steps</h2>
            <p className="tds-sub" style={{ maxWidth: "600px", margin: "0 auto" }}>
              ALG Data Guard manages the entire procurement journey — from spec capture to on-site deployment
              — leveraging TD SYNNEX's global supply chain at every stage.
            </p>
          </div>
          <div className="tds-journey__steps">
            {journey.map((step) => (
              <div key={step.num} className="tds-step">
                <div className="tds-step__num">{step.num}</div>
                <span className="tds-step__icon">{step.icon}</span>
                <div className="tds-step__title">{step.title}</div>
                <p className="tds-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="tds-industries">
        <div className="tds-shell">
          <div className="tds-industries__head">
            <div className="tds-tag tds-tag--dark"><span className="tds-dot" /> Industries Served</div>
            <h2 className="tds-title">Technology Procurement for Every Sector</h2>
            <p className="tds-sub tds-sub--dark" style={{ maxWidth: "600px", margin: "0 auto" }}>
              TD SYNNEX's global supply chain and ALG Data Guard's sector expertise means we can support
              organisations of any size, in any industry.
            </p>
          </div>
          <div className="tds-industries__grid">
            {industries.map((ind) => (
              <div key={ind.name} className="tds-ind-card">
                <span className="tds-ind-card__icon">{ind.icon}</span>
                <div>
                  <div className="tds-ind-card__name">{ind.name}</div>
                  <p className="tds-ind-card__desc">{ind.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="tds-faq">
        <div className="tds-shell">
          <div className="tds-faq__head">
            <div className="tds-tag tds-tag--orange" style={{ margin: "0 auto 1rem" }}><span className="tds-dot" /> FAQs</div>
            <h2 className="tds-title">TD SYNNEX Procurement Questions, Answered</h2>
            <p className="tds-sub" style={{ maxWidth: "600px", margin: "0 auto" }}>
              Expert answers about sourcing technology through ALG Data Guard and TD SYNNEX.
            </p>
          </div>
          <div className="tds-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`tds-faq-item${openFaq === i ? " open" : ""}`}>
                <button
                  className="tds-faq-item__trigger"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span className="tds-faq-item__q">{faq.q}</span>
                  <span className="tds-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="tds-faq-item__answer">
                  <div className="tds-faq-item__answer-inner">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="tds-cta">
        <div className="tds-cta__inner">
          <div className="tds-tag tds-tag--orange" style={{ margin: "0 auto 1.2rem" }}><span className="tds-dot" /> Get Started</div>
          <h2 className="tds-cta__title">
            Source Any Technology with <span>Speed and Confidence</span>
          </h2>
          <p className="tds-cta__sub">
            ALG Data Guard's TD SYNNEX partnership means you can procure virtually any IT product —
            with official warranties, competitive pricing, configuration services, and expert deployment support.
          </p>
          <div className="tds-cta__actions">
            <button className="tds-btn tds-btn--orange" onClick={() => navigate("/contact")}>
              Request a Quote <ArrowIcon />
            </button>
            <button className="tds-btn tds-btn--ghost" onClick={() => navigate("/contact")}>
              Talk to a Procurement Specialist
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}

